import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Calendar, Users, Clock, MapPin, CheckCircle, XCircle, Filter, ArrowLeft } from 'lucide-react';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MeetingsList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeSiteId } = useSite();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchMeetings();
  }, [statusFilter, typeFilter, activeSiteId]);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let url = `${API}/meetings`;
      const params = new URLSearchParams();

      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('meeting_type', typeFilter);
      if (activeSiteId) params.append('site_id', activeSiteId);

      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMeetings(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load meetings');
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'Completed') return <CheckCircle className="w-4 h-4" />;
    if (status === 'Cancelled') return <XCircle className="w-4 h-4" />;
    if (status === 'In Progress') return <Clock className="w-4 h-4 animate-pulse" />;
    return <Calendar className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Safety Meetings</h1>
            <p className="text-gray-600 mt-1">Schedule and manage safety meetings, toolbox talks, and committee sessions</p>
          </div>
          <div className="flex items-center gap-4">
            {hasPermission('meetings', 'create') && (
              <Button
                onClick={() => navigate('/meetings/create')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Schedule Meeting
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">Filters:</span>
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="Scheduled">Scheduled</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="Safety Committee">Safety Committee</option>
                <option value="Toolbox Talk">Toolbox Talk</option>
                <option value="Emergency Drill">Emergency Drill</option>
                <option value="Management Review">Management Review</option>
                <option value="Training Session">Training Session</option>
                <option value="Incident Review">Incident Review</option>
              </select>

              {(statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Meetings List */}
      <div className="grid grid-cols-1 gap-4">
        {meetings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No meetings found</p>
              {statusFilter === 'all' && typeFilter === 'all' && hasPermission('meetings', 'create') && (
                <Button
                  onClick={() => navigate('/meetings/create')}
                  className="mt-4"
                >
                  Schedule Your First Meeting
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          meetings.map((meeting) => (
            <Card
              key={meeting.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/meetings/${meeting.id}`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-gray-900">
                        {meeting.title}
                      </h3>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        {meeting.meeting_type}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(meeting.status)}`}>
                        {getStatusIcon(meeting.status)}
                        {meeting.status}
                      </span>
                      {meeting.is_mandatory && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          Mandatory
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <p className="font-semibold text-gray-900">
                            {formatDate(meeting.scheduled_date)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-500">Time:</span>
                          <p className="font-semibold text-gray-900">
                            {formatTime(meeting.scheduled_date)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-500">Location:</span>
                          <p className="font-semibold text-gray-900">{meeting.location}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-500">Attendees:</span>
                          <p className="font-semibold text-gray-900">
                            {meeting.attendees?.length || 0} invited
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-3">
                      <span className="text-sm text-gray-500">Organizer: </span>
                      <span className="text-sm font-semibold text-gray-900">{meeting.organizer_name}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
    </div>
  );
};

export default MeetingsList;